import { useEffect, useRef, useState } from "react";

import {
  clockLabelOf,
  elapsedMs,
  formatClockLabel,
  formatElapsed,
  formatTimeOfDay,
  halfHourPresets,
  hasUnsentTime,
  isStartPicked,
  startExistsOn,
  landedMs,
  parseElapsed,
  publishSeconds,
  startMs,
  tasksAwayLabel,
} from "../timesheet";
import type { RowOutcome, TaskListState, TimesheetRow as Row } from "../timesheet";

import styles from "./TimesheetRow.module.css";

const BAD_TIME = "Use HH:MM:SS";
/** The START list: 48 half hours, built once and shared by every row. */
const START_PRESETS: readonly { value: string; label: string }[] = halfHourPresets().map(
  (value) => ({ value, label: clockLabelOf(value) })
);
const START_AUTO_LABEL = "Automatic";
const SELECT_LABEL = "Publish this row";

interface TimesheetRowProps {
  row: Row;
  now: number;
  tasks: TaskListState;
  outcome?: RowOutcome | undefined;
  publishing?: boolean;
  selected?: boolean;
  onSelect?: ((id: string, next: boolean) => void) | undefined;
  onRename: (id: string, name: string) => void;
  onRetask: (id: string, taskId: string) => void;
  onToggle: (id: string) => void;
  onDelete?: ((id: string) => void) | undefined;
  onSetDate?: ((id: string, day: string) => void) | undefined;
  onSetElapsed?: ((id: string, ms: number) => void) | undefined;
  /**
   * Move the row's start to a 24-hour `HH:MM` on its own day. An empty string is
   * the Automatic option: it drops the pick and the row reads its own start.
   */
  onSetStart?: ((id: string, hhmm: string) => void) | undefined;
}

function shortDay(day: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (parts === null) return day;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${Number(parts[3])} ${months[Number(parts[2]) - 1] ?? ""}`;
}

function stateMark(
  row: Row,
  now: number,
  outcome?: RowOutcome
): { mark: string; title: string } | null {
  if (
    outcome?.kind === "failed" ||
    outcome?.kind === "landed-unconfirmed" ||
    outcome?.kind === "unnamed"
  ) {
    return { mark: "⚠", title: outcome.message };
  }
  if (outcome?.kind === "skipped") return { mark: "–", title: outcome.message };

  const landed = landedMs(row);
  if (landed === 0) return null;
  const elapsed = elapsedMs(row, now);
  const held = formatElapsed(landed);
  if (elapsed < landed) {
    return { mark: "⚠", title: `Accelo holds ${held} and cannot take time back.` };
  }
  const unsent = publishSeconds(row, now);
  return unsent === 0
    ? { mark: "✓", title: `In Accelo: ${held}. Nothing new to send.` }
    : { mark: "◐", title: `In Accelo: ${held}. A publish sends ${formatElapsed(unsent * 1000)}.` };
}

export function TimesheetRow({
  row,
  now,
  tasks,
  outcome,
  publishing = false,
  selected = false,
  onSelect,
  onRename,
  onRetask,
  onToggle,
  onDelete,
  onSetDate,
  onSetElapsed,
  onSetStart,
}: TimesheetRowProps) {
  const [editing, setEditing] = useState<"elapsed" | "date" | "start" | null>(null);
  const [draftDay, setDraftDay] = useState(row.date);
  const [draftTime, setDraftTime] = useState("");
  const [openedTime, setOpenedTime] = useState("");
  const [badTime, setBadTime] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const startRef = useRef<HTMLSelectElement>(null);
  const startButtonRef = useRef<HTMLButtonElement>(null);
  // Set when Enter or Escape closes START, so focus goes back to its button.
  const refocusStart = useRef(false);

  const elapsed = formatElapsed(elapsedMs(row, now));
  const started = formatClockLabel(startMs(row));
  const startPicked = isStartPicked(row);
  const needsTask = row.taskId === "";
  const mark = stateMark(row, now, outcome);
  const unknownTask =
    tasks.kind === "ready" &&
    row.taskId !== "" &&
    !tasks.groups.some((group) => group.tasks.some((task) => task.id === row.taskId));

  useEffect(() => {
    // One click opens the field AND its picker. Without this the person clicks
    // the cell, then clicks the calendar or the dropdown again to see anything.
    // showPicker() needs the click's own user activation, which still holds in
    // the effect React flushes after the handler. It throws where the browser
    // withholds it, and the control is focused and usable either way, so the
    // failure is deliberately silent.
    const control: { showPicker?: () => void } | null =
      editing === "date" ? dateRef.current : editing === "start" ? startRef.current : null;
    if (control === null) return;
    try {
      control.showPicker?.();
    } catch {
      // No picker on this browser, or no activation. The field still works.
    }
  }, [editing]);

  useEffect(() => {
    // Closing START unmounts the focused list. Without this the focus falls to
    // the page and a keyboard user loses their place in the row.
    if (editing !== null || !refocusStart.current) return;
    refocusStart.current = false;
    startButtonRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (!confirmingDelete) return;
    function cancelOutside(event: PointerEvent) {
      if (!confirmRef.current?.contains(event.target as Node)) setConfirmingDelete(false);
    }
    document.addEventListener("pointerdown", cancelOutside);
    return () => document.removeEventListener("pointerdown", cancelOutside);
  }, [confirmingDelete]);

  function openElapsed() {
    setDraftTime(elapsed);
    setOpenedTime(elapsed);
    setBadTime(false);
    setEditing("elapsed");
  }

  function saveElapsed() {
    if (draftTime === openedTime) {
      setEditing(null);
      return;
    }
    const ms = parseElapsed(draftTime);
    if (ms === null) {
      setBadTime(true);
      return;
    }
    onSetElapsed?.(row.id, ms);
    setEditing(null);
  }

  /**
   * Take a START value the moment it changes, and keep the list open. Where
   * the browser shows no list, each arrow key fires change, so closing here
   * would leave every later half hour out of reach. A start never moves the
   * row off the page, so taking each step is safe. Enter, Escape or leaving
   * the list closes it.
   */
  function chooseStart(value: string) {
    // "" is the Automatic option: it drops the pick and the row's own start returns.
    onSetStart?.(row.id, value);
  }

  function closeStart() {
    refocusStart.current = true;
    setEditing(null);
  }

  function openDate() {
    setDraftDay(row.date);
    setEditing("date");
  }

  /**
   * Commit the day on Enter or when the field is left, never on change. The
   * field fires change on every whole date: as its segments are typed, so
   * 10/05 over 09/18 reads 01/18 first, and on every arrow key in the open
   * calendar, with no keydown on the field. Neither is the day the person
   * meant, and committing one moves the row off the page mid-edit.
   */
  function saveDate() {
    if (draftDay !== "" && draftDay !== row.date) onSetDate?.(row.id, draftDay);
    setEditing(null);
  }

  return (
    <li className={`${styles.row} ${row.running ? styles.running : styles.paused}`}>
      <span className={styles.rail} />
      <div className={styles.lead}>
        {onSelect !== undefined && (
          <input
            type="checkbox"
            className={styles.check}
            checked={selected}
            onChange={(event) => onSelect(row.id, event.target.checked)}
            disabled={publishing || !hasUnsentTime(row, now)}
            aria-label={SELECT_LABEL}
          />
        )}
        <input
          type="text"
          className={styles.name}
          value={row.name}
          onChange={(event) => onRename(row.id, event.target.value)}
          placeholder="What are you working on?"
          aria-label="Task name"
          disabled={publishing}
        />
      </div>
      <div className={styles.task}>
        <select
          className={`${styles.select} ${needsTask ? styles.selectUnset : ""}`}
          value={row.taskId}
          onChange={(event) => onRetask(row.id, event.target.value)}
          disabled={tasks.kind !== "ready" || publishing}
          aria-label="Accelo task to log this time against"
        >
          {tasks.kind === "ready" ? (
            <>
              <option value="">Not set yet</option>
              {unknownTask && (
                <option
                  value={row.taskId}
                  disabled
                >{`Task ${row.taskId}, no longer in your list`}</option>
              )}
              {tasks.groups.map((group) => (
                <optgroup key={group.projectId} label={group.client}>
                  {group.tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </optgroup>
              ))}
            </>
          ) : (
            <option value={row.taskId}>{tasksAwayLabel(tasks.kind)}</option>
          )}
        </select>
      </div>
      <div className={styles.date}>
        {editing === "date" ? (
          <input
            autoFocus
            ref={dateRef}
            type="date"
            className={styles.dateInput}
            value={draftDay}
            aria-label="Date"
            onChange={(event) => setDraftDay(event.target.value)}
            onBlur={saveDate}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveDate();
              if (event.key === "Escape") setEditing(null);
            }}
          />
        ) : (
          <button
            type="button"
            className={styles.dateButton}
            onClick={openDate}
            disabled={publishing || onSetDate === undefined}
            aria-label="Edit row date"
            // The day is not written in the row: the page shows one day at a
            // time and the day selector names it. Hovering still reads it out.
            title={shortDay(row.date)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.calendarIcon}>
              <rect
                x="3"
                y="5"
                width="18"
                height="16"
                rx="2.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
              <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>
      <div className={styles.span}>
        {editing === "start" ? (
          <select
            autoFocus
            ref={startRef}
            className={styles.startSelect}
            value={startPicked ? formatTimeOfDay(startMs(row)) : ""}
            aria-label="Start time"
            onChange={(event) => chooseStart(event.target.value)}
            onBlur={() => setEditing(null)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== "Escape") return;
              // The Enter would go on to click the START button that takes
              // focus, and open the list again.
              event.preventDefault();
              closeStart();
            }}
          >
            <option value="">{START_AUTO_LABEL}</option>
            {START_PRESETS.map((preset) => (
              <option
                key={preset.value}
                value={preset.value}
                // A half hour the clock skips on this day cannot be a start, and
                // a disabled option is one the arrow keys step over.
                disabled={!startExistsOn(row.date, preset.value)}
              >
                {preset.label}
              </option>
            ))}
          </select>
        ) : (
          <button
            ref={startButtonRef}
            type="button"
            className={`${styles.startButton} ${startPicked ? styles.startPicked : styles.startAuto}`}
            onClick={() => setEditing("start")}
            disabled={publishing || onSetStart === undefined}
            aria-label="Edit start time"
            title={
              startPicked
                ? "The start you picked. Choose Automatic to use the time the row began."
                : "The time this row began. Pick a start to override it."
            }
          >
            {started}
          </button>
        )}
      </div>
      <div className={styles.elapsed}>
        {row.running && <span className={styles.liveDot} />}
        {editing === "elapsed" ? (
          <input
            autoFocus
            type="text"
            className={`${styles.inlineInput} ${badTime ? styles.invalid : ""}`}
            value={draftTime}
            aria-label="Elapsed time, HH:MM:SS"
            onFocus={(event) => event.target.select()}
            aria-invalid={badTime}
            title={badTime ? BAD_TIME : undefined}
            onChange={(event) => {
              setDraftTime(event.target.value);
              setBadTime(false);
            }}
            onBlur={saveElapsed}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveElapsed();
              if (event.key === "Escape") setEditing(null);
            }}
          />
        ) : (
          <button
            type="button"
            className={styles.timeButton}
            onClick={openElapsed}
            disabled={publishing || onSetElapsed === undefined}
            aria-label="Edit elapsed time"
          >
            <span className={styles.time} role="timer" aria-label="Elapsed time">
              {elapsed}
            </span>
          </button>
        )}
      </div>
      <span className={styles.state} title={mark?.title} aria-label={mark?.title}>
        {mark?.mark}
      </span>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => onToggle(row.id)}
        disabled={publishing}
        aria-label={row.running ? "Pause the timer for this row" : "Start the timer for this row"}
      >
        {row.running ? (
          <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.pauseIcon}>
            <rect x="6" y="5" width="4" height="14" rx="1.2" />
            <rect x="14" y="5" width="4" height="14" rx="1.2" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.playIcon}>
            <path d="M8 5.5l11 6.5-11 6.5z" />
          </svg>
        )}
      </button>
      {onDelete === undefined ? (
        <span />
      ) : confirmingDelete ? (
        <button
          ref={confirmRef}
          type="button"
          className={styles.confirmDelete}
          onClick={() => onDelete(row.id)}
          onBlur={() => setConfirmingDelete(false)}
          aria-label="Confirm delete"
        >
          ✓
        </button>
      ) : (
        <button
          type="button"
          className={styles.delete}
          onClick={() => setConfirmingDelete(true)}
          disabled={publishing}
          aria-label="Delete this row"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.trashIcon}>
            <path d="M4.5 7h15" />
            <path d="M9.5 7V4.8h5V7" />
            <path d="M6.5 7l.9 12.2a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
            <path d="M10.3 10.7v6.6" />
            <path d="M13.7 10.7v6.6" />
          </svg>
        </button>
      )}
    </li>
  );
}
